import { useState, useEffect, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faCheck,
  faGripVertical,
} from "@awesome.me/kit-70c1f0055f/icons/classic/light";
import {
  Dialog,
  DialogTitle,
  DialogBody,
  DialogActions,
} from "@/components/ui/dialog/Dialog";
import { Button } from "@/components/ui/button/Button";
import {
  STATISTIC_FIELD_LABELS,
  STATISTICS_TABS,
  buildFrequencyStatId,
  parseFrequencyStatId,
  getFrequencyStatLabel,
  RELATIVE_STATISTICS,
  generatePeriodDefinitions,
  TRAILING_PERIOD_LABELS,
  type StatisticField,
  type TrailingPeriod,
  type StatisticsTabId,
  type FrequencyStatisticId,
  type StatsViewColumn,
  type StatisticColumn,
  type PeriodReturnColumn,
  type StatFrequency,
} from "@/services/api/statistics/types";

// Combined item type for both statistics and periods
interface SelectableItem {
  id: string;
  name: string;
  group: string;
  type: "statistic" | "period";
  isRelative?: boolean;
}

interface StatisticsSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  columns: StatsViewColumn[];
  onColumnsChange: (columns: StatsViewColumn[]) => void;
  asOfDate?: Date;
}

// Convert a column to a unique string ID for internal tracking
function columnToId(col: StatsViewColumn): string {
  if (col.type === "period_return") {
    return `period:${(col as PeriodReturnColumn).period}`;
  }
  const statCol = col as StatisticColumn;
  if (statCol.lookback) {
    return `stat:${statCol.field}:trailing_${statCol.lookback}`;
  }
  if (statCol.frequency) {
    return `stat:${statCol.field}:${statCol.frequency}`;
  }
  return `stat:${statCol.field}`;
}

// Convert a string ID back to a column object
function idToColumn(id: string): StatsViewColumn {
  if (id.startsWith("period:")) {
    const period = id.slice(7);
    return { type: "period_return", period };
  }
  // Parse stat ID: stat:field or stat:field:frequency or stat:field:trailing_period
  const parts = id.slice(5).split(":");
  const field = parts[0] as StatisticField;
  const modifier = parts[1];
  
  if (modifier?.startsWith("trailing_")) {
    const lookback = modifier.slice(9);
    return { type: "statistic", field, lookback };
  }
  if (modifier) {
    return { type: "statistic", field, frequency: modifier as StatFrequency };
  }
  return { type: "statistic", field };
}

export function StatisticsSelector({
  isOpen,
  onClose,
  columns,
  onColumnsChange,
  asOfDate = new Date(),
}: StatisticsSelectorProps) {
  // Generate period definitions based on as-of date
  const periodDefinitions = useMemo(
    () => generatePeriodDefinitions(asOfDate),
    [asOfDate]
  );

  // Build period items for selection
  const periodItems: SelectableItem[] = useMemo(
    () =>
      periodDefinitions.map((p) => ({
        id: p.id,
        name: p.name,
        group: p.group,
        type: "period" as const,
      })),
    [periodDefinitions]
  );

  // Local state for selections - unified ordered list of column IDs
  const [orderedSelection, setOrderedSelection] = useState<string[]>(() => {
    return columns.map(columnToId);
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [draggedItem, setDraggedItem] = useState<string | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<StatisticsTabId | "periods">("performance");

  // Derived state: extract stats and periods from unified list
  const localSelectedStats = useMemo(() => 
    orderedSelection.filter(item => item.startsWith("stat:")),
    [orderedSelection]
  );
  
  const localSelectedPeriods = useMemo(() =>
    orderedSelection.filter(item => item.startsWith("period:")),
    [orderedSelection]
  );

  // Get items for the current tab
  const currentTabItems = useMemo((): SelectableItem[] => {
    if (activeTab === "periods") {
      return periodItems;
    }
    
    const tab = STATISTICS_TABS[activeTab];
    if (!tab) return [];
    
    const items: SelectableItem[] = [];
    
    // Handle trailing periods tab specially
    if ("isTrailingPeriod" in tab && tab.isTrailingPeriod) {
      Object.entries(tab.groups).forEach(([periodKey, group]) => {
        group.stats.forEach((field) => {
          const trailingPeriod = periodKey as TrailingPeriod;
          const id = buildFrequencyStatId(field, null, trailingPeriod);
          const isRelative = RELATIVE_STATISTICS.includes(field);
          items.push({
            id,
            name: STATISTIC_FIELD_LABELS[field] || field,
            group: TRAILING_PERIOD_LABELS[trailingPeriod],
            type: "statistic",
            isRelative,
          });
        });
      });
    } else {
      // Regular frequency-based tabs
      Object.entries(tab.groups).forEach(([, group]) => {
        group.stats.forEach((field) => {
          const id = buildFrequencyStatId(field, tab.frequency);
          const isRelative = RELATIVE_STATISTICS.includes(field);
          items.push({
            id,
            name: STATISTIC_FIELD_LABELS[field] || field,
            group: group.label,
            type: "statistic",
            isRelative,
          });
        });
      });
    }
    return items;
  }, [activeTab, periodItems]);

  // Group items by their group property
  const groupedItems = useMemo(() => {
    return currentTabItems.reduce(
      (acc, item) => {
        if (!acc[item.group]) {
          acc[item.group] = [];
        }
        acc[item.group].push(item);
        return acc;
      },
      {} as Record<string, SelectableItem[]>
    );
  }, [currentTabItems]);

  // Reset local state when props change
  useEffect(() => {
    setOrderedSelection(columns.map(columnToId));
  }, [columns]);

  // Filter based on search term
  const filteredGroups = useMemo(() => {
    return Object.entries(groupedItems).reduce(
      (acc, [group, items]) => {
        const filtered = items.filter((item) =>
          item.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
        if (filtered.length > 0) {
          acc[group] = filtered;
        }
        return acc;
      },
      {} as Record<string, SelectableItem[]>
    );
  }, [groupedItems, searchTerm]);

  // Build the ID for an item in the same format we use for column IDs
  const getItemId = (item: SelectableItem): string => {
    if (item.type === "period") {
      return `period:${item.id}`;
    }
    // For statistics, the item.id is already in FrequencyStatisticId format (field:frequency or field:trailing_period)
    // We need to convert to our internal format
    const { field, frequency, trailingPeriod } = parseFrequencyStatId(item.id as FrequencyStatisticId);
    if (trailingPeriod) {
      return `stat:${field}:trailing_${trailingPeriod}`;
    }
    if (frequency) {
      return `stat:${field}:${frequency}`;
    }
    return `stat:${field}`;
  };

  const isSelected = (item: SelectableItem): boolean => {
    const itemId = getItemId(item);
    return orderedSelection.includes(itemId);
  };

  const handleToggle = (item: SelectableItem) => {
    const itemId = getItemId(item);
    
    setOrderedSelection((prev) => {
      if (prev.includes(itemId)) {
        return prev.filter((id) => id !== itemId);
      }
      return [...prev, itemId];
    });
  };

  const handleSelectAllInTab = () => {
    if (activeTab === "periods") {
      // Add all periods that aren't already selected
      const periodIds = periodItems.map((p) => `period:${p.id}`);
      setOrderedSelection(prev => {
        const existing = new Set(prev);
        const toAdd = periodIds.filter(id => !existing.has(id));
        return [...prev, ...toAdd];
      });
    } else {
      // Add all stats from this tab that aren't already selected
      const tabItems = currentTabItems.filter(i => i.type === "statistic");
      const statIds = tabItems.map(item => getItemId(item));
      setOrderedSelection(prev => {
        const existing = new Set(prev);
        const toAdd = statIds.filter(id => !existing.has(id));
        return [...prev, ...toAdd];
      });
    }
  };

  const handleDeselectAllInTab = () => {
    if (activeTab === "periods") {
      // Remove all periods
      setOrderedSelection(prev => prev.filter(id => !id.startsWith("period:")));
    } else {
      // Remove all stats from this tab
      const tabItemIds = new Set(currentTabItems.map(i => getItemId(i)));
      setOrderedSelection(prev => prev.filter(id => !tabItemIds.has(id)));
    }
  };

  const handleApply = () => {
    // Convert ordered selection back to columns
    const newColumns = orderedSelection.map(idToColumn);
    onColumnsChange(newColumns);
    onClose();
  };

  // Build all selected items for the right pane with proper labels
  // This preserves the unified ordering from orderedSelection
  const allSelected = useMemo(() => {
    return orderedSelection.map(itemId => {
      if (itemId.startsWith("period:")) {
        const periodId = itemId.slice(7);
        const periodItem = periodItems.find((p) => p.id === periodId);
        return periodItem || {
          id: itemId,
          name: periodId,
          group: "Periods",
          type: "period" as const,
        };
      } else {
        // Parse stat:field:modifier format
        const parts = itemId.slice(5).split(":");
        const field = parts[0] as StatisticField;
        const modifier = parts[1];
        
        // Build a FrequencyStatisticId for display
        let displayId: FrequencyStatisticId;
        if (modifier?.startsWith("trailing_")) {
          displayId = `${field}:${modifier}` as FrequencyStatisticId;
        } else if (modifier) {
          displayId = `${field}:${modifier}` as FrequencyStatisticId;
        } else {
          displayId = field as FrequencyStatisticId;
        }
        
        const isRelative = RELATIVE_STATISTICS.includes(field);
        return {
          id: itemId,
          name: getFrequencyStatLabel(displayId),
          group: "Statistics",
          type: "statistic" as const,
          isRelative,
        };
      }
    });
  }, [orderedSelection, periodItems]);

  // Drag handlers for reordering
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDraggedItem(itemId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragEnd = () => {
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverIndex(index);
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (!draggedItem) return;

    // draggedItem is now the full internal ID (stat:field:freq or period:id)
    const dragIndex = orderedSelection.indexOf(draggedItem);
    if (dragIndex === -1) return;
    
    const newSelection = [...orderedSelection];
    newSelection.splice(dragIndex, 1);
    
    // Adjust drop index if dragging from before to after
    const adjustedDropIndex = dropIndex > dragIndex ? dropIndex - 1 : dropIndex;
    newSelection.splice(Math.max(0, Math.min(adjustedDropIndex, newSelection.length)), 0, draggedItem);
    
    setOrderedSelection(newSelection);
    setDraggedItem(null);
    setDragOverIndex(null);
  };

  // Count selected per tab
  const getTabCount = (tabId: StatisticsTabId | "periods"): number => {
    if (tabId === "periods") return localSelectedPeriods.length;
    
    const tab = STATISTICS_TABS[tabId];
    if (!tab) return 0;
    
    let count = 0;
    
    // Handle trailing periods tab
    if ("isTrailingPeriod" in tab && tab.isTrailingPeriod) {
      Object.entries(tab.groups).forEach(([periodKey, group]) => {
        group.stats.forEach((field) => {
          const internalId = `stat:${field}:trailing_${periodKey}`;
          if (orderedSelection.includes(internalId)) count++;
        });
      });
    } else {
      Object.values(tab.groups).forEach((group) => {
        group.stats.forEach((field) => {
          const internalId = `stat:${field}:${tab.frequency}`;
          if (orderedSelection.includes(internalId)) count++;
        });
      });
    }
    return count;
  };

  const totalSelected = localSelectedStats.length + localSelectedPeriods.length;

  // Tab configuration
  const tabs: Array<{ id: StatisticsTabId | "periods"; label: string; description?: string }> = [
    { id: "performance", label: "Performance", description: STATISTICS_TABS.performance.description },
    { id: "daily", label: "Daily", description: STATISTICS_TABS.daily.description },
    { id: "monthly", label: "Monthly", description: STATISTICS_TABS.monthly.description },
    { id: "quarterly", label: "Quarterly", description: STATISTICS_TABS.quarterly.description },
    { id: "trailing", label: "Trailing", description: STATISTICS_TABS.trailing.description },
    { id: "periods", label: "Periods", description: "Calendar period returns" },
  ];

  return (
    <Dialog open={isOpen} onClose={onClose} size="3xl">
      <DialogTitle>Select Statistics & Period Returns</DialogTitle>
      <DialogBody>
        <div className="space-y-4">
          {/* Tabs for switching between statistic types */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 overflow-x-auto">
            {tabs.map((tab) => {
              const count = getTabCount(tab.id);
              return (
                <button
                  key={tab.id}
                  className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-indigo-500 text-indigo-600 dark:text-indigo-400"
                      : "border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                  onClick={() => setActiveTab(tab.id)}
                  title={tab.description}
                >
                  {tab.label}
                  {count > 0 && (
                    <span className="ml-1.5 px-1.5 py-0.5 text-xs rounded-full bg-indigo-100 dark:bg-indigo-900 text-indigo-600 dark:text-indigo-300">
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab description */}
          {activeTab !== "periods" && STATISTICS_TABS[activeTab] && (
            <p className="text-xs text-gray-500 dark:text-gray-400 italic">
              {STATISTICS_TABS[activeTab].description}
            </p>
          )}

          {/* Search */}
          <div>
            <input
              type="text"
              placeholder={`Search ${activeTab === "periods" ? "periods" : "statistics"}...`}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-4" style={{ minHeight: "400px" }}>
            {/* Available Items */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Available
                </h3>
                <div className="flex gap-2">
                  <Button onClick={handleSelectAllInTab} plain>
                    Select All
                  </Button>
                  <Button onClick={handleDeselectAllInTab} plain>
                    Clear
                  </Button>
                </div>
              </div>
              <div className="overflow-y-auto max-h-[340px] space-y-1">
                {Object.entries(filteredGroups).map(([group, items]) => (
                  <div key={group} className="mb-4">
                    <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wider sticky top-0 bg-white dark:bg-gray-800 py-1">
                      {group}
                      <span className="ml-2 text-gray-400">({items.length})</span>
                    </h4>
                    <div className="space-y-0.5">
                      {items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded cursor-pointer"
                          onClick={() => handleToggle(item)}
                          title={
                            item.isRelative
                              ? "Calculated vs resource's primary benchmark"
                              : undefined
                          }
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {item.name}
                              {item.isRelative && (
                                <span className="ml-2 text-xs text-indigo-400">
                                  (vs benchmark)
                                </span>
                              )}
                            </div>
                          </div>
                          <div
                            className={`w-5 h-5 flex-shrink-0 flex items-center justify-center rounded border ml-2 ${
                              isSelected(item)
                                ? "bg-indigo-600 border-indigo-600"
                                : "border-gray-300 dark:border-gray-600"
                            }`}
                          >
                            {isSelected(item) && (
                              <FontAwesomeIcon
                                icon={faCheck}
                                className="h-3 w-3 text-white"
                              />
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Selected Items */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Selected ({totalSelected})
                </h3>
              </div>
              <div className="overflow-y-auto max-h-[340px]">
                {allSelected.length === 0 ? (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-8">
                    No items selected
                  </p>
                ) : (
                  <div className="space-y-0.5">
                    {allSelected.map((item, index) => (
                      <div
                        key={item.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, item.id)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => handleDragOver(e, index)}
                        onDrop={(e) => handleDrop(e, index)}
                        className={`flex items-center p-2 rounded cursor-move transition-all ${
                          draggedItem === item.id ? "opacity-50" : ""
                        } ${
                          dragOverIndex === index
                            ? "border-t-2 border-indigo-500"
                            : ""
                        } hover:bg-gray-100 dark:hover:bg-gray-700`}
                      >
                        <FontAwesomeIcon
                          icon={faGripVertical}
                          className="h-4 w-4 text-gray-400 mr-3 flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {item.name}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {item.type === "period" ? "📅 Period Return" : "📊 Statistic"}
                            {item.isRelative && " • vs benchmark"}
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(item);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0"
                        >
                          <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button onClick={onClose} plain>
          Cancel
        </Button>
        <Button onClick={handleApply} color="indigo">
          Apply ({totalSelected} selected)
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default StatisticsSelector;
